import { Modal } from 'antd';
import moment from 'moment';
import {
  AiOutlineCheckCircle,
  AiOutlineClockCircle,
  AiOutlineEye,
  AiOutlineFile,
  AiOutlineInbox,
} from 'react-icons/ai';
import { HiOutlineChevronRight } from 'react-icons/hi';

import { isPaymentRequired } from '../../../helpers/isPaymentRequired';
import { useMobileVersion } from '../../../hooks/useMobileVersion';
import { ObligationFile } from '../../../operations/file/types';
import { FileStatus } from '../Calendar';
import {
  EmptyState,
  FileAction,
  FileCard,
  FileDescription,
  FileDueDate,
  FileIcon,
  FileInfo,
  ModalContent,
  ModalHeader,
} from './styles';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  files: ObligationFile[];
  openModal: (file: ObligationFile) => void;
  status: FileStatus;
};

export const FilesModal = ({
  isVisible,
  onClose,
  files,
  openModal,
  status,
}: Props) => {
  const isMobile = useMobileVersion();

  const filesInfo = {
    expired: {
      title: 'Documentos Vencidos',
      color: 'var(--danger)',
      icon: <AiOutlineClockCircle />,
      emptyText: 'Nenhum documento vencido',
    },
    visualized: {
      title: 'Documentos Baixados',
      color: 'var(--success)',
      icon: <AiOutlineCheckCircle />,
      emptyText: 'Nenhum documento baixado',
    },
    unviewed: {
      title: 'Documentos Não Baixados',
      color: 'var(--info)',
      icon: <AiOutlineEye />,
      emptyText: 'Nenhum documento pendente',
    },
  };

  const currentFilesInfo = filesInfo[status];

  const formatDueDate = (file: ObligationFile) => {
    const date = moment(file.due_at);
    const today = moment();
    const requiresPayment = isPaymentRequired(file.requires_payment);

    if (date.isSame(today, 'day')) {
      return requiresPayment ? 'Vence hoje' : 'Disponível hoje';
    }
    if (date.isBefore(today, 'day')) {
      return requiresPayment
        ? `Venceu em ${date.format('DD/MM/YYYY')}`
        : `Disponível desde ${date.format('DD/MM/YYYY')}`;
    }
    return requiresPayment
      ? `Vence em ${date.format('DD/MM/YYYY')}`
      : `Disponível em ${date.format('DD/MM/YYYY')}`;
  };

  const handleFileClick = (file: ObligationFile) => {
    openModal(file);
    onClose();
  };

  return (
    <Modal
      title={
        <ModalHeader $color={currentFilesInfo.color}>
          <span className="status-badge">
            {currentFilesInfo.icon}
            {filesInfo[status].title}
          </span>
          <span className="file-count">
            {files.length} {files.length === 1 ? 'documento' : 'documentos'}
          </span>
        </ModalHeader>
      }
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={isMobile ? 'calc(100vw - 32px)' : 480}
    >
      {files.length === 0 ? (
        <EmptyState>
          <AiOutlineInbox />
          <p>{currentFilesInfo.emptyText}</p>
        </EmptyState>
      ) : (
        <ModalContent>
          {files.map((file) => (
            <FileCard
              key={file.id}
              $color={currentFilesInfo.color}
              onClick={() => handleFileClick(file)}
            >
              <FileIcon $color={currentFilesInfo.color}>
                <AiOutlineFile />
              </FileIcon>
              <FileInfo>
                <FileDescription>{file.description}</FileDescription>
                <FileDueDate>{formatDueDate(file)}</FileDueDate>
              </FileInfo>
              <FileAction $color={currentFilesInfo.color}>
                <HiOutlineChevronRight />
              </FileAction>
            </FileCard>
          ))}
        </ModalContent>
      )}
    </Modal>
  );
};
